// The debrief at the show (module 10 U162, U190 to U192; B1703): record one on a meeting of yours, and read
// the debriefs on a meeting. Only our own person's voice: the line above the button says so, and the button
// is drawn only on a meeting the reader is one of ours on, once it has started, and only while debriefs are
// switched on for the store.
import { ActionButton } from '../parts/index.js';
import { useEffect, useRef, useState } from 'react';
import { showApi, type Debrief, type ShowSheet } from './api.js';
import { clockOfSeconds, debriefHead, debriefStateWords } from './words.js';

/** A debrief is about a minute (U162); at ten minutes the recording stops by itself and is sent. */
const MAX_SECONDS = 600;
/** Words longer than this fold behind "Read all". */
const FOLD_AT = 320;

/** The first type this browser can record, in the order the supplier takes them as they are. */
function recorderType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const t of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']) {
    if (MediaRecorder.isTypeSupported?.(t)) return t;
  }
  return undefined;
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'asking' }
  | { kind: 'recording'; seconds: number }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'error'; message: string };

export function DebriefRecorder({ store, s, meetingId, onSent }: { store: string; s: ShowSheet; meetingId: string; onSent: () => void }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const rec = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<number | null>(null);

  const release = () => {
    if (timer.current !== null) { window.clearInterval(timer.current); timer.current = null; }
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  };
  useEffect(() => () => { rec.current?.state === 'recording' && rec.current.stop(); release(); }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setPhase({ kind: 'error', message: 'This browser cannot record. Open the page in Safari or Chrome on your phone.' });
      return;
    }
    setPhase({ kind: 'asking' });
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setPhase({ kind: 'error', message: 'The microphone was not allowed, so nothing was recorded.' });
      return;
    }
    const type = recorderType();
    const r = new MediaRecorder(stream.current, type ? { mimeType: type } : undefined);
    const chunks: Blob[] = [];
    r.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
    r.onstop = async () => {
      release();
      const blob = new Blob(chunks, { type: r.mimeType || type || 'audio/webm' });
      setPhase({ kind: 'sending' });
      try {
        await showApi.debrief(store, s.show.id, meetingId, blob);
        setPhase({ kind: 'sent' });
        onSent();
      } catch (e) {
        setPhase({ kind: 'error', message: `${e instanceof Error ? e.message.replace(/^\d{3}\s*/, '') : 'It was not sent.'} The recording was not kept.` });
      }
    };
    rec.current = r;
    r.start(1000);
    const began = Date.now();
    setPhase({ kind: 'recording', seconds: 0 });
    timer.current = window.setInterval(() => {
      const seconds = (Date.now() - began) / 1000;
      if (seconds >= MAX_SECONDS && r.state === 'recording') { r.stop(); return; }
      setPhase({ kind: 'recording', seconds });
    }, 250);
  };
  const stop = () => { if (rec.current?.state === 'recording') rec.current.stop(); };

  return (
    <div className="sh-rec">
      {phase.kind === 'recording'
        ? (
          <>
            <span className="sh-rec-on" aria-live="off"><span className="sh-rec-dot" aria-hidden="true" />Recording {clockOfSeconds(phase.seconds)}</span>
            <ActionButton type="button" className="sh-rec-go" onClick={stop}>Stop and send</ActionButton>
          </>
        )
        : phase.kind === 'asking' ? <span className="sh-meta">Allow the microphone to start.</span>
        : phase.kind === 'sending' ? <span className="sh-meta" role="status">Sending…</span>
        : phase.kind === 'sent' ? <span className="sh-meta" role="status">Sent. Its text will appear under After the show.</span>
        : (
          <>
            <ActionButton type="button" className="sh-rec-go" onClick={start}>Record a debrief</ActionButton>
            <span className="sh-meta">Your own voice only, after the meeting.</span>
          </>
        )}
      {phase.kind === 'error' ? <span className="sh-late" role="alert">{phase.message}</span> : null}
    </div>
  );
}

/** The debriefs on one meeting, as the reader may see them. */
export function DebriefList({ items, timeZone }: { items: Debrief[]; timeZone: string }) {
  if (items.length === 0) return null;
  return <div className="sh-dbs">{items.map((d) => <DebriefItem key={d.id} d={d} timeZone={timeZone} />)}</div>;
}

function DebriefItem({ d, timeZone }: { d: Debrief; timeZone: string }) {
  const [open, setOpen] = useState(false);
  const state = debriefStateWords(d);
  const long = (d.words ?? '').length > FOLD_AT;
  const shown = d.words && long && !open ? `${d.words.slice(0, FOLD_AT).replace(/\s+\S*$/, '')}…` : d.words;
  return (
    <div className="sh-db">
      <small>{debriefHead(d, timeZone)}</small>
      {state ? <span className={state.tone === 'warn' ? 'sh-late' : 'sh-meta'}>{state.text}</span> : null}
      {shown ? <p className="sh-db-words">{shown}</p> : null}
      {long ? <ActionButton reading type="button" className="sh-deal" onClick={() => setOpen(!open)}>{open ? 'Show less' : 'Read all'}</ActionButton> : null}
    </div>
  );
}
